import { type QueryResult, type OperationVariables } from '@apollo/react-common';
import type { ApolloQueryResult } from 'apollo-client';
import {
	type QueryHookOptions,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- `@confluence/apollo-relay-interop` package is the only one where such imports are allowed
	useQuery,
} from '@apollo/react-hooks';
import { type GraphQLTaggedNode, useRelayEnvironment } from 'react-relay';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { type ConcreteRequest, createOperationDescriptor, getRequest } from 'relay-runtime';
import { type DefinitionNode } from 'graphql';
import isEqual from 'lodash/isEqual';

import UFOInteractionContext from '@atlaskit/react-ufo/interaction-context';

import { expValEquals } from '@confluence/feature-experiments';
import { useSuspenseBoundaryName } from '@confluence/query-preloader-boundary/entry-points/suspenseBoundaryChain.ts';
import { verifyQueryBoundary } from '@confluence/query-preloader-boundary/entry-points/developmentQueryBoundaryVerification.ts';
import { useStableCallback } from '@confluence/use-stable/entry-points/useStableCallback.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import { isRelayEnabled } from './isRelayEnabled.ts';
import { fetchQueryWithErrorPolicy } from './fetchQuery.ts';
import {
	isNoStoreFetchPolicy,
	mapFetchPolicyForQuery,
	type QueryInteropFetchPolicy,
} from './mapFetchPolicyForQuery.ts';
import { mapFetchPolicyForApollo } from './mapFetchPolicyForApollo.ts';
import { releaseRelayQueryPreloadRetentionOnHookUnmount } from './relayQueryPreloadRetention.ts';
import { useMemoObj } from './useMemoObj.ts';
import { toDocumentNode } from './toDocumentNode.ts';
import type { ApolloInteropMutableHelper } from './ApolloInteropMutableHelper.ts';
import { NetworkStatus } from './NetworkStatus.ts';
import { useApolloQueryCacheBridgeSync } from './cacheBridge/useApolloQueryCacheBridgeSync.ts';
import { syncQueryCacheBridge } from './cacheBridge/queryCacheBridgeSync.ts';
import { getRelayOperationName } from './relayOperation.ts';
import { useDeferredQueryNotifications } from './useDeferredQueryNotifications.ts';

export type RefetchSupportedType<TData, TVariables> = (
	vars?: Partial<TVariables>,
) => Promise<Pick<ApolloQueryResult<TData>, 'data'> & { error?: ApolloErrorInterop }>;

/**
 * Options for the fetchMore function, mirroring Apollo v2's FetchMoreOptions.
 * @see https://www.apollographql.com/docs/react/v2/data/pagination/
 */
export type FetchMoreOptions<TData, TVariables> = {
	/**
	 * Variables to use for the fetchMore query. These are merged with the original variables.
	 */
	variables?: Partial<TVariables>;
	/**
	 * Function to merge the previous query result with the fetchMore result.
	 * This is required and is where you implement your pagination merge logic.
	 * Note: Uses ApolloInteropMutableHelper to support mutable updates on Relay's readonly types.
	 */
	updateQuery: (
		previousResult: ApolloInteropMutableHelper<TData>,
		options: { fetchMoreResult?: TData; variables?: TVariables },
	) => TData;
};

export type FetchMoreType<TData, TVariables> = (
	options: FetchMoreOptions<TData, TVariables>,
) => Promise<ApolloQueryResult<TData>>;

export type UseQueryInteropSupportedResult<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = Pick<
	QueryResult<TData, TVariables>,
	'data' | 'loading' | 'variables' | 'startPolling' | 'stopPolling'
> & {
	refetch: RefetchSupportedType<TData, TVariables>;
	fetchMore: FetchMoreType<TData, TVariables>;
	error?: ApolloErrorInterop;
	/**
	 * The current network status of the query.
	 * @see NetworkStatus enum for possible values
	 */
	networkStatus: NetworkStatus;
};

export type UseQueryInteropSupportedHookOptions<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = Pick<
	QueryHookOptions<TData, TVariables>,
	| 'variables'
	| 'skip'
	| 'onError'
	| 'errorPolicy'
	| 'pollInterval'
	| 'notifyOnNetworkStatusChange'
	| 'onError'
	| 'onCompleted'
	| 'context'
> & {
	/**
	 * Fetch policy for the initial fetch. Apollo-only values
	 * ('cache-and-network', 'standby') are mapped to the closest Relay equivalent.
	 */
	fetchPolicy?: QueryInteropFetchPolicy;
	/**
	 * Fetch policy used by subsequent automatic fetches. Explicit `refetch` and `fetchMore`
	 * operations always consult the network, matching Apollo.
	 */
	nextFetchPolicy?: QueryInteropFetchPolicy;
	/**
	 * Ensures that the interop in Apollo build variant uses the AGG client
	 */
	interopApolloAggClient?: boolean;
	/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
	__callerPackage__?: string;
};

export type UseQueryInteropSupportedArgs<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> =
	| [GraphQLTaggedNode]
	| [GraphQLTaggedNode, UseQueryInteropSupportedHookOptions<TData, TVariables>];

type QueryResultSource<TVariables> = {
	query: GraphQLTaggedNode;
	variables: TVariables;
};

type QueryDataState<TData, TVariables> = {
	data: TData | undefined;
	source: QueryResultSource<TVariables> | null;
};

type QueryErrorState<TVariables> = {
	error: any;
	source: QueryResultSource<TVariables> | null;
};

type ImperativeResultSource<TVariables> = QueryResultSource<TVariables> & {
	baseVariables: TVariables;
};

const shouldShowLoadingForInitialFetch = (
	fetchPolicy: QueryInteropFetchPolicy | undefined,
	hasCompleteInitialSnapshot: boolean,
): boolean => {
	if (!hasCompleteInitialSnapshot) {
		return true;
	}

	return (
		fetchPolicy === 'network-only' ||
		fetchPolicy === 'cache-and-network' ||
		fetchPolicy === 'no-cache' ||
		// Belt-and-braces: a 'no-store' caller never seeds `initialSnapshot`, so the early return
		// above already fires. Listed anyway so this predicate stays correct on its own terms rather
		// than depending on how the caller happens to compute `hasCompleteInitialSnapshot`.
		fetchPolicy === 'no-store'
	);
};

function isBoundaryVerificationEnabled() {
	return (
		process.env.CLOUD_ENV === 'dev' ||
		process.env.CLOUD_ENV === 'branch' ||
		process.env.CLOUD_ENV === 'staging'
	);
}

function tryVerifyRelayQueryBoundary<TVariables extends Record<string, any>>(
	query: GraphQLTaggedNode,
	variables: TVariables,
	currentSuspenseBoundaries: string[],
) {
	if (
		!isBoundaryVerificationEnabled() ||
		typeof window === 'undefined' ||
		!window.__SSR_REACT_STREAMING__
	) {
		return;
	}

	try {
		verifyQueryBoundary(toDocumentNode(query), variables, currentSuspenseBoundaries);
	} catch {
		// Boundary verification is a development diagnostic and should never affect Relay rendering.
	}
}

export function useQueryInterop<TData, TVariables extends Record<string, any> = OperationVariables>(
	...[query, params]: UseQueryInteropSupportedArgs<TData, TVariables>
): UseQueryInteropSupportedResult<TData, TVariables> {
	const operationName = getRelayOperationName(query);
	if (isRelayEnabled(params?.__callerPackage__, { operationName, phase: 'component' })) {
		// eslint-disable-next-line react-hooks/rules-of-hooks -- Apollo/Relay route is branch-stable for a given caller package.
		return useQueryInteropRelay<TData, TVariables>(query, params);
	}

	// eslint-disable-next-line react-hooks/rules-of-hooks -- Apollo/Relay route is branch-stable for a given caller package.
	return useQueryInteropApollo<TData, TVariables>(query, params);
}

function useQueryInteropApollo<TData, TVariables extends Record<string, any> = OperationVariables>(
	query: GraphQLTaggedNode,
	params?: UseQueryInteropSupportedHookOptions<TData, TVariables>,
): UseQueryInteropSupportedResult<TData, TVariables> {
	const { interopApolloAggClient, nextFetchPolicy, ...apolloOptions } = params ?? {};

	// `fetchPolicy` reaches Apollo by spread, so interop-only values must be translated here or they
	// silently fall through to Apollo's default cache behaviour. Only 'no-store' is affected.
	const apolloFetchPolicy = mapFetchPolicyForApollo(apolloOptions.fetchPolicy);
	const apolloNextFetchPolicy = mapFetchPolicyForApollo(nextFetchPolicy);

	const documentNode = toDocumentNode(query);
	const {
		getVariables: getCacheBridgeVariables,
		isEnabled: isCacheBridgeEnabled,
		onCompleted: onApolloCompletedWithCacheBridge,
		syncObservedResult,
	} = useApolloQueryCacheBridgeSync<TData, TVariables>({
		query,
		variables: apolloOptions.variables,
		fetchPolicy: apolloFetchPolicy,
		useAggClient: interopApolloAggClient,
		onCompleted: apolloOptions.onCompleted,
		callerPackage: params?.__callerPackage__,
	});
	const cacheBridgeOptionVariables = getCacheBridgeVariables();
	const onApolloCompletedWithCacheBridgeVariables = useCallback(
		(completedData: TData) =>
			onApolloCompletedWithCacheBridge(completedData, cacheBridgeOptionVariables),
		[cacheBridgeOptionVariables, onApolloCompletedWithCacheBridge],
	);
	const onApolloCompleted = isCacheBridgeEnabled
		? onApolloCompletedWithCacheBridgeVariables
		: apolloOptions.onCompleted;

	const apolloResult = useQuery<TData, TVariables>(
		...[
			documentNode,
			{
				...apolloOptions,
				fetchPolicy: apolloFetchPolicy,
				...(onApolloCompleted ? { onCompleted: onApolloCompleted } : {}),
				...(apolloNextFetchPolicy ? { nextFetchPolicy: apolloNextFetchPolicy } : {}),
				...(interopApolloAggClient
					? {
							client:
								window['GLOBAL_APOLLO_AGG_CLIENT'] ?? (window as any)['MOCK_GATEWAY_APOLLO_CLIENT'],
						}
					: {}),
				context: {
					...apolloOptions?.context,
					requestId: (query as ConcreteRequest).params?.id,
				},
			},
		],
	);
	const apolloData = apolloResult.data;
	const apolloError = apolloResult.error;
	const apolloSkip = apolloOptions.skip;
	const apolloVariables = getCacheBridgeVariables(apolloResult.variables);

	useEffect(() => {
		if (!isCacheBridgeEnabled) {
			return;
		}

		syncObservedResult({
			skip: apolloSkip,
			variables: apolloVariables,
			data: apolloData,
			error: apolloError,
		});
	}, [
		apolloData,
		apolloError,
		apolloSkip,
		apolloVariables,
		isCacheBridgeEnabled,
		syncObservedResult,
	]);

	// Note: UFO holds for the Apollo path are handled automatically by the
	// parcel-resolver-spa-module-replacements which replaces @apollo/react-hooks
	// with @atlassian/ufo-apollo-log/react-hooks at build time.

	// Hide 'client' on the apolloResult object so callers cannot access the Apollo
	// client instance directly via the hook result. We use Object.defineProperty
	// instead of spreading to avoid creating a new object reference on every render,
	// which would cause components using componentDidUpdate to loop infinitely.
	Object.defineProperty(apolloResult, 'client', {
		get() {
			return undefined;
		},
		enumerable: false,
		configurable: true,
	});
	return apolloResult as unknown as UseQueryInteropSupportedResult<TData, TVariables>;
}

function useQueryInteropRelay<TData, TVariables extends Record<string, any> = OperationVariables>(
	query: GraphQLTaggedNode,
	options?: UseQueryInteropSupportedHookOptions<TData, TVariables>,
): UseQueryInteropSupportedResult<TData, TVariables> {
	const environment = useRelayEnvironment();
	const currentSuspenseBoundaries = useSuspenseBoundaryName();
	const operationName = getRelayOperationName(query);

	// Extract variables and memoize them to prevent unnecessary re-renders
	const variables = useMemoObj<TVariables>(options?.variables, options?.__callerPackage__, {
		operationName,
		phase: 'memo',
	});
	const context = useMemoObj(options?.context ?? {}, options?.__callerPackage__, {
		operationName,
		phase: 'memo',
	});

	if (!('params' in query)) {
		const operationName =
			'definitions' in query
				? ((query as { definitions: readonly DefinitionNode[] }).definitions?.find(
						(d): d is DefinitionNode & { name?: { value: string } } =>
							d.kind === 'OperationDefinition' && 'name' in d,
					)?.name?.value ?? 'unknown')
				: 'unknown';
		throw new Error(
			`[Relay Migration] unsupported tagged node provided for operation "${operationName}". Please make sure you generated Relay artifacts for your operation`,
		);
	}

	tryVerifyRelayQueryBoundary(query, variables, currentSuspenseBoundaries);

	const skip = options?.skip || false;
	const notifyOnNetworkStatusChange = options?.notifyOnNetworkStatusChange || false;
	const pollInterval = options?.pollInterval ?? 0;
	const errorPolicy = options?.errorPolicy ?? 'none';

	// `'no-store'` must suppress store *reads* as well as writes: the store is how other operations'
	// records reach this hook, which is half of what the policy is for. Derived from the initial
	// policy only — a query either participates in the store or it does not, so letting
	// `nextFetchPolicy` flip it mid-flight would leave a half-populated record behind.
	const noStore = isNoStoreFetchPolicy(options?.fetchPolicy);

	// Apollo defaults to cache-first when no policy is provided. Map the omitted
	// policy explicitly so Relay uses the equivalent store-or-network behavior
	// instead of fetchQuery's network-only default.
	const initialFetchPolicy = mapFetchPolicyForQuery(options?.fetchPolicy);
	const subsequentFetchPolicy = options?.nextFetchPolicy
		? mapFetchPolicyForQuery(options.nextFetchPolicy)
		: options?.fetchPolicy
			? initialFetchPolicy
			: undefined;
	// Polling defaults to network-only to match Apollo.
	const pollFetchPolicy = subsequentFetchPolicy ?? 'network-only';
	const cacheBridgeFetchPolicy = options?.fetchPolicy;
	const cacheBridgeSubsequentFetchPolicy = options?.nextFetchPolicy ?? cacheBridgeFetchPolicy;
	const interopApolloAggClient = options?.interopApolloAggClient;
	const relayRequestOperationName = (query as ConcreteRequest).params?.name ?? 'unnamed';
	const operation = useMemo(
		() => (skip ? null : createOperationDescriptor(getRequest(query), variables)),
		[query, skip, variables],
	);
	// Seeding from the store is a *read*, so 'no-store' skips it. Without this the hook would hand
	// back records written by other operations — exactly the pollution the policy exists to avoid.
	const initialSnapshot =
		operation && !noStore ? environment.lookup(operation.fragment) : undefined;
	const hasCompleteInitialSnapshot = Boolean(
		operation && initialSnapshot && !initialSnapshot.isMissingData,
	);
	const initialData = hasCompleteInitialSnapshot ? (initialSnapshot?.data as TData) : undefined;
	const initialFetchLoadingRef = useRef<{
		operation: typeof operation;
		fetchPolicy: QueryInteropFetchPolicy | undefined;
		showLoading: boolean;
	} | null>(null);
	// Cache completeness can change after the initial fetch writes to the Relay
	// store. Keep this decision stable for the operation so that completion does
	// not restart the initial fetch effect.
	if (
		initialFetchLoadingRef.current?.operation !== operation ||
		initialFetchLoadingRef.current?.fetchPolicy !== options?.fetchPolicy
	) {
		initialFetchLoadingRef.current = {
			operation,
			fetchPolicy: options?.fetchPolicy,
			showLoading: shouldShowLoadingForInitialFetch(
				options?.fetchPolicy,
				hasCompleteInitialSnapshot,
			),
		};
	}
	const showLoadingForInitialFetch = initialFetchLoadingRef.current.showLoading;
	const initialNetworkStatus =
		skip || !showLoadingForInitialFetch ? NetworkStatus.ready : NetworkStatus.loading;

	// Track whether the initial fetch has completed so we can switch to nextFetchPolicy
	const hasCompletedInitialFetch = useRef(false);

	// State to manage the query result
	const [resultState, setResultState] = useState<QueryDataState<TData, TVariables>>({
		data: initialData,
		source: hasCompleteInitialSnapshot ? { query, variables } : null,
	});
	// A fetchMore request can outlive the render that started it. Keep the latest accepted result
	// available synchronously so cache writes during that request are included in updateQuery.
	const latestResultDataRef = useRef<TData | undefined>(initialData);
	const updateResultState = useCallback((nextState: QueryDataState<TData, TVariables>) => {
		latestResultDataRef.current = nextState.data;
		setResultState(nextState);
	}, []);
	const [loading, setLoading] = useState(initialNetworkStatus === NetworkStatus.loading);
	const [errorState, setErrorState] = useState<QueryErrorState<TVariables>>({
		error: undefined,
		source: null,
	});
	const [networkStatus, setNetworkStatus] = useState<NetworkStatus>(initialNetworkStatus);
	const [imperativeResultSource, setImperativeResultSource] =
		useState<ImperativeResultSource<TVariables> | null>(null);
	const data = resultState.data;
	const error = errorState.error;
	const onError = useStableCallback(options?.onError || function noop() {});
	const onCompleted = useStableCallback(options?.onCompleted || function noop() {});

	// Defers onCompleted/onError to a post-commit effect so callers observe the state
	// that describes the result being reported, matching Apollo. `queueNotification` is
	// referentially stable and is therefore safe to list as an effect dependency below.
	const { queueNotification, markLoading } = useDeferredQueryNotifications<TData>({
		onCompleted,
		onError,
		hasCallbacks: Boolean(options?.onCompleted || options?.onError),
	});

	const mirrorRelayResultToApollo = useCallback(
		(
			relayResult: TData | undefined,
			resultVariables: TVariables = variables,
			fetchPolicy: QueryInteropFetchPolicy | undefined = cacheBridgeFetchPolicy,
		) => {
			// Relay state has already been updated; mirror the successful result
			// into Apollo as secondary cache bridge work.
			syncQueryCacheBridge({
				source: 'relay',
				query,
				variables: resultVariables,
				data: relayResult,
				fetchPolicy,
				useAggClient: interopApolloAggClient,
			});
		},
		[cacheBridgeFetchPolicy, interopApolloAggClient, query, variables],
	);

	// Refs keep startPolling/stopPolling identity-stable across renders.
	const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const latestEnvRef = useRef(environment);
	const latestQueryRef = useRef(query);
	const latestVarsRef = useRef(variables);
	const latestContextRef = useRef(context);
	const isMountedRef = useRef(true);

	useEffect(() => {
		latestEnvRef.current = environment;
		latestQueryRef.current = query;
		latestVarsRef.current = variables;
		latestContextRef.current = context;
	}, [environment, query, variables, context]);

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	const stopPolling = useCallback(() => {
		if (pollTimerRef.current !== null) {
			clearInterval(pollTimerRef.current);
			pollTimerRef.current = null;
		}
	}, []);

	const runPollTick = useCallback(() => {
		// When notifyOnNetworkStatusChange is opted-in, mirror Apollo: flip
		// networkStatus to `poll` and `loading` to true during the in-flight tick.
		if (notifyOnNetworkStatusChange) {
			setNetworkStatus(NetworkStatus.poll);
			setLoading(true);
		}
		if (expValEquals('confluence_relay_interop_poll_parity', 'isEnabled', true)) {
			// Reset the error state to a new object only when there is an error to clear. Writing a
			// fresh "no error" object on every tick enqueues a state update each interval, forcing a
			// re-render of every subscriber (and its subtree) even when nothing changed. Returning the
			// existing reference lets React bail out of the update when the error is already clear.
			setErrorState((prev) =>
				prev.error === undefined && prev.source === null
					? prev
					: { error: undefined, source: null },
			);
		} else {
			setErrorState({ error: undefined, source: null });
		}
		fetchQueryWithErrorPolicy<{ response: TData; variables: TVariables }>(
			latestEnvRef.current,
			latestQueryRef.current,
			latestVarsRef.current,
			{
				fetchPolicy: pollFetchPolicy,
				networkCacheConfig: { metadata: latestContextRef.current },
			},
			{ errorPolicy, noStore },
		)
			.toPromise()
			.then((result) => {
				if (!isMountedRef.current) return;
				if (notifyOnNetworkStatusChange) {
					setNetworkStatus(NetworkStatus.ready);
					setLoading(false);
				}
				// Guard against empty completions: don't wipe data or invoke
				// onCompleted with undefined.
				if (result?.data !== undefined) {
					const source = {
						query: latestQueryRef.current,
						variables: latestVarsRef.current,
					};
					if (
						!result.error &&
						isEqual(result.data, latestResultDataRef.current) &&
						expValEquals('confluence_relay_interop_poll_parity', 'isEnabled', true)
					) {
						// The tick resolved to data equal to what is already committed. Committing it
						// again would hand subscribers a new reference and re-render them (and their
						// subtrees) with no change, so skip the state/cache writes and the completed
						// notification and keep the current committed result.
						return;
					}
					updateResultState({ data: result.data as TData, source });
					mirrorRelayResultToApollo(
						result.data as TData,
						latestVarsRef.current,
						cacheBridgeSubsequentFetchPolicy,
					);

					if (result.error) {
						setErrorState({ error: result.error, source });
						queueNotification({ type: 'error', error: result.error, identity: source });
					} else {
						setErrorState({ error: undefined, source: null });
						queueNotification({
							type: 'completed',
							data: result.data as TData,
							identity: source,
						});
					}
				}
			})
			.catch((err) => {
				if (!isMountedRef.current) return;
				setErrorState({
					error: err,
					source: {
						query: latestQueryRef.current,
						variables: latestVarsRef.current,
					},
				});
				if (notifyOnNetworkStatusChange) {
					setNetworkStatus(NetworkStatus.ready);
					setLoading(false);
				}
				queueNotification({
					type: 'error',
					error: err,
					identity: {
						query: latestQueryRef.current,
						variables: latestVarsRef.current,
					},
				});
				// Match Apollo: keep polling on errors.
			});
	}, [
		mirrorRelayResultToApollo,
		cacheBridgeSubsequentFetchPolicy,
		notifyOnNetworkStatusChange,
		queueNotification,
		pollFetchPolicy,
		errorPolicy,
		noStore,
		updateResultState,
	]);

	const startPolling = useCallback(
		(interval: number) => {
			// No-op for non-positive intervals (Apollo parity).
			if (typeof interval !== 'number' || interval <= 0) {
				return;
			}
			// Skip on the server — guard before any destructive timer ops.
			if (typeof window === 'undefined') {
				return;
			}
			// Clear any existing timer so the new interval takes effect.
			if (pollTimerRef.current !== null) {
				clearInterval(pollTimerRef.current);
				pollTimerRef.current = null;
			}
			pollTimerRef.current = setInterval(runPollTick, interval);
		},
		[runPollTick],
	);

	// Auto-start when pollInterval is set and not skipped.
	useEffect(() => {
		if (skip || !pollInterval || pollInterval <= 0) {
			stopPolling();
			return;
		}
		startPolling(pollInterval);
		return stopPolling;
	}, [skip, pollInterval, startPolling, stopPolling]);

	// Hold the UFO interaction while the Relay query is loading.
	// This mirrors what @atlassian/ufo-apollo-log does for Apollo queries via
	// the parcel-resolver-spa-module-replacements build-time replacement.
	const ufoContext = useContext(UFOInteractionContext);

	useEffect(() => {
		if (loading && networkStatus !== NetworkStatus.poll) {
			return ufoContext?.hold(relayRequestOperationName);
		}
	}, [loading, networkStatus, relayRequestOperationName, ufoContext]);

	// Use fetchQuery to get data
	useEffect(() => {
		// Treat each dep-change re-run as a fresh initial fetch.
		// Explicit `refetch`/`fetchMore` calls always consult the network separately.
		hasCompletedInitialFetch.current = false;
		setImperativeResultSource(null);

		if (skip) {
			updateResultState({ data: undefined, source: { query, variables } });
			setErrorState({ error: undefined, source: null });
			setLoading(false);
			setNetworkStatus(NetworkStatus.ready);
			return;
		}
		if (!operation) {
			updateResultState({ data: undefined, source: { query, variables } });
			setErrorState({ error: undefined, source: null });
			setLoading(false);
			setNetworkStatus(NetworkStatus.ready);
			return;
		}

		setLoading(showLoadingForInitialFetch);
		setErrorState({ error: undefined, source: null });
		setNetworkStatus(showLoadingForInitialFetch ? NetworkStatus.loading : NetworkStatus.ready);
		if (showLoadingForInitialFetch) {
			// The only place a loading transition is recorded. Poll ticks and refetches
			// deliberately do not: Apollo applies their data without reporting it, so
			// recording a transition there would bypass the dedup guard and over-report.
			markLoading();
		}

		// Subscribe to the Relay store so cache writes (e.g. writeQueryInterop/commitUpdate
		// after a mutation) re-render consumers, matching Apollo's cache-watching useQuery.
		//
		// Skipped for 'no-store': there is no record to retain (nothing was written), and
		// subscribing would feed this hook *other* operations' writes to the same ids — the exact
		// cross-operation pollution the policy exists to prevent. The trade-off is deliberate:
		// these queries stop reacting to mutations elsewhere and must refetch explicitly.
		const retainDisposable = noStore ? null : environment.retain(operation);
		const storeSubscription = noStore
			? null
			: environment.subscribe(environment.lookup(operation.fragment), (snapshot) => {
					if (hasCompletedInitialFetch.current && !snapshot.isMissingData && isMountedRef.current) {
						updateResultState({ data: snapshot.data as TData, source: { query, variables } });
						setErrorState({ error: undefined, source: null });
					}
				});

		const subscription = fetchQueryWithErrorPolicy<{ response: TData; variables: TVariables }>(
			environment,
			query,
			variables,
			{
				fetchPolicy: initialFetchPolicy,
				networkCacheConfig: { metadata: context },
			},
			{ errorPolicy, noStore },
		).subscribe({
			next: (result) => {
				hasCompletedInitialFetch.current = true;
				mirrorRelayResultToApollo(result.data as TData);
				updateResultState({ data: result.data, source: { query, variables } });
				setLoading(false);
				setNetworkStatus(NetworkStatus.ready);

				if (result.error) {
					setErrorState({ error: result.error, source: { query, variables } });
					queueNotification({ type: 'error', error: result.error, identity: { query, variables } });
				} else {
					setErrorState({ error: undefined, source: null });
					queueNotification({
						type: 'completed',
						data: result.data as TData,
						identity: { query, variables },
					});
				}
			},
			error: (err: any) => {
				hasCompletedInitialFetch.current = true;
				setErrorState({ error: err, source: { query, variables } });
				setLoading(false);
				// Note: Apollo v2 keeps networkStatus at ready on errors, only sets the error field
				setNetworkStatus(NetworkStatus.ready);
				queueNotification({ type: 'error', error: err, identity: { query, variables } });
			},
		});

		return () => {
			subscription.unsubscribe();
			storeSubscription?.dispose();
			retainDisposable?.dispose();
			if (retainDisposable) {
				releaseRelayQueryPreloadRetentionOnHookUnmount(environment, operation.request.identifier);
			}
		};
	}, [
		environment,
		query,
		variables,
		skip,
		queueNotification,
		markLoading,
		context,
		initialFetchPolicy,
		showLoadingForInitialFetch,
		mirrorRelayResultToApollo,
		operation,
		errorPolicy,
		noStore,
		updateResultState,
	]);

	// Create a refetch function for Apollo compatibility
	const refetch: RefetchSupportedType<TData, TVariables> = useCallback(
		(newVariables?: Partial<TVariables>) => {
			// Apollo merges partial refetch variables into the variables used by the original
			// observable query. Preserve that behavior in Relay so omitted variables do not reset
			// to their GraphQL defaults.
			const varsToUse = newVariables
				? ({ ...variables, ...newVariables } as TVariables)
				: variables;
			setErrorState({ error: undefined, source: null });
			if (notifyOnNetworkStatusChange) {
				setLoading(true);
				setNetworkStatus(NetworkStatus.refetch);
			}

			return fetchQueryWithErrorPolicy<{ response: TData; variables: TVariables }>(
				environment,
				query,
				varsToUse,
				{
					// Apollo refetch always consults the network, regardless of the observable
					// query's current fetch policy. Using store-or-network here can return complete
					// but stale Relay data without issuing a request.
					fetchPolicy: 'network-only',
					networkCacheConfig: { metadata: context },
				},
				{ errorPolicy, noStore },
			)
				.toPromise()
				.then((result) => {
					// A refetch can resolve without a value (the underlying observable completing without
					// emitting), and committing `data: undefined` under a source matching the current
					// variables would wipe the data permanently: `isResultForCurrentVariables` becomes true,
					// which disables the retention below, so `stableDataRef` is overwritten with
					// `undefined`. Apollo's equivalent falls back to `getLastResult().data`; keep the last
					// accepted result and only move the source forward.
					//
					// The returned promise still reports what the network actually produced, so a caller
					// can tell an empty refetch from a populated one.
					const hasRefetchedData = result?.data !== undefined;
					if (hasRefetchedData) {
						mirrorRelayResultToApollo(
							result.data as TData,
							varsToUse,
							cacheBridgeSubsequentFetchPolicy,
						);
					}
					const source = { query, variables: varsToUse };
					updateResultState({
						data: hasRefetchedData ? (result.data as TData) : latestResultDataRef.current,
						source,
					});
					setImperativeResultSource({ ...source, baseVariables: variables });
					setLoading(false);
					setNetworkStatus(NetworkStatus.ready);
					if (result?.error) {
						setErrorState({ error: result.error, source });
					} else {
						setErrorState({ error: undefined, source: null });
					}
					return {
						data: result?.data as TData,
						error: result?.error,
						loading: false,
						networkStatus: NetworkStatus.ready,
						stale: false,
					};
				})
				.catch((err) => {
					const source = { query, variables: varsToUse };
					setImperativeResultSource({ ...source, baseVariables: variables });
					setErrorState({ error: err, source });
					setLoading(false);
					setNetworkStatus(NetworkStatus.error);
					throw err;
				});
		},
		[
			environment,
			query,
			variables,
			context,
			mirrorRelayResultToApollo,
			cacheBridgeSubsequentFetchPolicy,
			errorPolicy,
			noStore,
			notifyOnNetworkStatusChange,
			updateResultState,
		],
	);

	// Create a fetchMore function for pagination support
	const fetchMore: FetchMoreType<TData, TVariables> = useCallback(
		(fetchMoreOpts: FetchMoreOptions<TData, TVariables>) => {
			// Merge the new variables with existing variables
			const fetchMoreVariables = {
				...variables,
				...fetchMoreOpts.variables,
			} as TVariables;

			// Only set loading/networkStatus if notifyOnNetworkStatusChange is enabled
			// This matches Apollo v2 behavior where loading stays false during fetchMore
			// unless explicitly opting in to network status notifications
			setErrorState({ error: undefined, source: null });
			if (notifyOnNetworkStatusChange) {
				setLoading(true);
				setNetworkStatus(NetworkStatus.fetchMore);
			}

			return fetchQueryWithErrorPolicy<{ response: TData; variables: TVariables }>(
				environment,
				query,
				fetchMoreVariables,
				{
					// Apollo fetchMore always consults the network. Reusing a complete Relay
					// snapshot here would return the current page instead of fetching the next one.
					fetchPolicy: 'network-only',
					networkCacheConfig: { metadata: context },
				},
				{ errorPolicy, noStore },
			)
				.toPromise()
				.then((fetchMoreResult) => {
					// Use updateQuery to merge the new results with existing data
					// Pass empty object if data is undefined (matches Apollo v2 behavior)
					const mergedData = fetchMoreOpts.updateQuery(
						latestResultDataRef.current ?? ({} as TData),
						{
							fetchMoreResult: fetchMoreResult?.data as TData,
							variables: fetchMoreVariables,
						},
					);
					// Persist Apollo's updateQuery result under the original operation. This keeps
					// the Relay store subscription on the same merged result so later record writes
					// remain reactive without reverting the list to a single page.
					//
					// This is a store write in its own right, independent of the fetch layer, so the
					// `noStore` flag passed to `fetchQueryWithErrorPolicy` above does not cover it —
					// it has to be guarded here or 'no-store' would leak the merged page into the
					// store. Skipping it is safe because there is no store subscription to keep in
					// sync: `mergedData` is held in `resultState`/`latestResultDataRef` instead.
					if (operation && !noStore) {
						environment.commitPayload(operation, mergedData as Record<string, unknown>);
					}
					mirrorRelayResultToApollo(mergedData, variables, cacheBridgeSubsequentFetchPolicy);
					const source = { query, variables };
					updateResultState({ data: mergedData, source });
					setImperativeResultSource(null);
					if (notifyOnNetworkStatusChange) {
						setLoading(false);
						setNetworkStatus(NetworkStatus.ready);
					}
					if (fetchMoreResult?.error) {
						setErrorState({ error: fetchMoreResult.error, source });
					} else {
						setErrorState({ error: undefined, source: null });
					}
					return {
						data: mergedData,
						error: fetchMoreResult?.error,
						loading: false,
						networkStatus: NetworkStatus.ready,
						stale: false,
					};
				})
				.catch((err) => {
					setResultState((currentResultState) =>
						currentResultState.source
							? currentResultState
							: {
									data: currentResultState.data,
									source: { query, variables },
								},
					);
					if (notifyOnNetworkStatusChange) {
						setLoading(false);
						setNetworkStatus(NetworkStatus.ready);
					}
					throw err;
				});
		},
		[
			environment,
			query,
			variables,
			operation,
			context,
			mirrorRelayResultToApollo,
			notifyOnNetworkStatusChange,
			cacheBridgeSubsequentFetchPolicy,
			errorPolicy,
			noStore,
			updateResultState,
		],
	);

	const currentDataVariables =
		imperativeResultSource?.query === query && imperativeResultSource.baseVariables === variables
			? imperativeResultSource.variables
			: variables;
	const isResultForCurrentVariables =
		resultState.source?.query === query && resultState.source?.variables === currentDataVariables;
	const isErrorForCurrentVariables =
		errorState.source?.query === query && errorState.source?.variables === currentDataVariables;
	const isWaitingForCurrentVariables = Boolean(
		!skip &&
		operation &&
		!isErrorForCurrentVariables &&
		!isResultForCurrentVariables &&
		!hasCompleteInitialSnapshot,
	);
	// Seeding from the store after a failed fetch is deliberate and safe: `operation` is built from the
	// *current* variables, so a store read can only ever be data for the current query+variables —
	// never the cross-variables staleness that a retained value can be. Do not "fix" this to suppress
	// data on error; it withholds validly-cached records and diverges from Apollo further, not less.
	const rawEffectiveData = isResultForCurrentVariables ? data : initialData;
	// Apollo parity: `useQuery` hands back the SAME `data` reference across renders while the
	// underlying data is unchanged. Relay does not — `environment.lookup(...)` and store snapshots
	// produce a fresh object on every read, so `initialData` churns every render and the store
	// subscription pushes new references for structurally-equal snapshots. Consumers that dedupe by
	// reference (e.g. `@confluence/comments-data-manager`, whose `data === lastProcessedData` guard
	// gates a store write that re-renders it) would otherwise reprocess forever and crash with
	// React error #185 ("Maximum update depth exceeded"). Preserve the previous reference whenever
	// the data is structurally equal so the reference only changes when the data actually changes.
	//
	// `lastRawDataRef` tracks the last raw reference we evaluated so the deep `isEqual` runs only
	// when the incoming reference actually changes. Without it, an equal-but-new re-emission would
	// leave `stableDataRef.current` (old) permanently diverged from `rawEffectiveData` (new stable
	// state), re-running the deep compare on every subsequent unrelated re-render.
	const stableDataRef = useRef<TData | undefined>(rawEffectiveData);
	const lastRawDataRef = useRef<TData | undefined>(rawEffectiveData);
	if (lastRawDataRef.current !== rawEffectiveData) {
		lastRawDataRef.current = rawEffectiveData;
		// Apollo parity: `QueryData.getQueryResult` retains the previous result's `data` for the whole
		// loading window (`if (loading) { result.data = previousData || data }`) and only clears
		// `previousData.result` on unmount — never on a variables change. So Apollo is
		// stale-while-loading, not blank-while-loading; this is measured, not inferred, by the
		// dual-variant suite in `apollo-relay-interop-tests`.
		//
		// Blanking here would make consumers take their loading branch and unmount the content
		// subtree, so the render that commits the new data becomes a full mount instead of an
		// in-place update — landing that cost on the vc90 critical path, and breaking `React.memo`
		// bail-outs down the prop chain on the way in.
		//
		// Safe because `effectiveLoading` below is forced true for this same render. That synchronous
		// `loading` flip is what actually fixes the EOP recommendation redirect: the bug was a
		// committed render carrying stale data *and* `loading: false`, because `setLoading(true)` only
		// ran in the fetch effect.
		const retainPreviousWhileWaiting =
			rawEffectiveData === undefined && isWaitingForCurrentVariables;
		if (!retainPreviousWhileWaiting && !isEqual(stableDataRef.current, rawEffectiveData)) {
			stableDataRef.current = rawEffectiveData;
		}
	}
	const effectiveData = stableDataRef.current;
	const effectiveLoading =
		skip || !operation || isErrorForCurrentVariables
			? false
			: isWaitingForCurrentVariables
				? true
				: loading;
	// Apollo distinguishes the two loading windows, and it was measured reporting `setVariables` (2)
	// for a variables change: `loading` (1) means "has never been run before", `setVariables` means
	// "variables changed and that query is in flight" — exactly what the enum members document.
	// Retained data is the signal for which window this is: if the hook has previously handed back
	// data, a result settled before, so this is a transition rather than a first run.
	//
	// Only `ready`/`loading` are overridden. `refetch`, `fetchMore` and `poll` describe an in-flight
	// imperative operation that outranks this, so they pass through untouched.
	const isOverridableNetworkStatus =
		networkStatus === NetworkStatus.ready || networkStatus === NetworkStatus.loading;
	const effectiveNetworkStatus =
		isWaitingForCurrentVariables && isOverridableNetworkStatus
			? stableDataRef.current !== undefined
				? NetworkStatus.setVariables
				: NetworkStatus.loading
			: networkStatus;
	const effectiveError = isErrorForCurrentVariables ? error : undefined;

	// Preserve Apollo-compatible result identity across unrelated renders. Some consumers use the
	// whole query result in hook dependency arrays, so a fresh wrapper can cause update loops.
	const result = useMemo(
		() => ({
			data: effectiveData,
			loading: effectiveLoading,
			error: effectiveError,
			refetch,
			fetchMore,
			variables,
			networkStatus: effectiveNetworkStatus,
			startPolling,
			stopPolling,
		}),
		[
			effectiveData,
			effectiveLoading,
			effectiveError,
			refetch,
			fetchMore,
			variables,
			effectiveNetworkStatus,
			startPolling,
			stopPolling,
		],
	);
	return result as UseQueryInteropSupportedResult<TData, TVariables>;
}
